<template>
  <div>
    <b-modal
      id="subsection-inputs-modal"
      title="Working Group Updates"
      centered
      size="xl"
      no-close-on-backdrop
      no-close-on-esc
      @hidden="resetSubsectionInputsModal"
    >
      <b-row v-if="formattedActions.length">
        <b-col cols="12">
          <app-collapse accordion :type="'border'" class="mb-2">
            <app-collapse-item title="Connected Actions">
              <div v-if="formattedActions.length">
                <div
                  v-for="(action, index) in formattedActions"
                  :key="index"
                  class="border-left mb-2 pl-2"
                  :style="{
                    borderLeftColor: action.pillarColor + ' !important',
                  }"
                >
                  <div
                    class="font-weight-bold"
                    :style="{ color: action.pillarColor }"
                  >
                    {{ action.displayText.pillarTitle || "-" }}
                  </div>
                  <div class="small text-muted">
                    {{ action.displayText.remainingText }}
                  </div>
                </div>
              </div>
              <div v-else class="text-muted text-center py-2">
                No connected actions available
              </div>
            </app-collapse-item>
          </app-collapse>
        </b-col>
      </b-row>

      <b-table
        v-if="groupedSubsections.length"
        :items="groupedSubsections"
        :fields="subsectionFields"
        responsive
        striped
        class="mt-2"
        style="max-height: 300px; overflow-y: auto"
        show-empty
        empty-text="No matching records found"
        small
      >
        <!-- Action Info Column -->
        <template #cell(meeting_title)="data">
          <div class="truncate-text" :id="`meeting-${data.index}`">
            {{ data.item.meeting_title ? data.item.meeting_title : "-" }}
          </div>
          <b-popover
            v-if="data.item.meeting_title"
            :target="`meeting-${data.index}`"
            triggers="hover"
            placement="auto"
            boundary="viewport"
            delay="500"
          >
            <template #title>Title</template>
            <div class="popover-content">
              {{ data.item.meeting_title }}
            </div>
          </b-popover>
        </template>

        <template #cell(additional_comment)="data">
          <div class="truncate-text" :id="`comment-${data.index}`">
            {{
              data.item.additional_comment ? data.item.additional_comment : "-"
            }}
          </div>
          <b-popover
            v-if="data.item.additional_comment"
            :target="`comment-${data.index}`"
            triggers="hover"
            placement="auto"
            boundary="viewport"
            delay="500"
          >
            <template #title>Comments</template>
            <div class="popover-content">
              {{ data.item.additional_comment }}
            </div>
          </b-popover>
        </template>

        <template #cell(commence_date)="data">
          {{ formatDate(data.item.commence_date) }}
        </template>

        <template #cell(meeting_minutes_file)="data">
          <b-button
            v-if="data.item.meeting_minutes_file"
            variant="outline-primary"
            size="sm"
            @click="downloadFile(data.item.id)"
          >
            <feather-icon icon="DownloadIcon" size="16" />
            Download
          </b-button>
          <span v-else>No file</span>
        </template>
      </b-table>

      <b-alert variant="dark" show v-if="groupedSubsections.length === 0">
        <h4 class="alert-heading">No updates found</h4>
        <div class="alert-body">
          <span>This group has no updates</span>
        </div>
      </b-alert>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="$bvModal.hide('subsection-inputs-modal')"
        >
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axiosIns from "@axios";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";

export default {
  name: "WorkingGroupSubsections",

  components: {
    ValidationProvider,
    ValidationObserver,
    flatPickr,
    vSelect,
    AppCollapse,
    AppCollapseItem,
  },

  props: {},

  data() {
    return {
      subsectionInputsForm: {
        id: null,
        working_group_id: null,
        meeting_title: "",
        owner_id: null,
        commence_date: null,
        end_date: null,
        meeting_minutes_file: null,
        additional_comment: "",
      },
      isEditMode: false,
      selectedWorkingGroupId: null,
      wg_subsections: [],
      subsection_actions: [],
      subsectionFields: [
        {
          key: "meeting_title",
          label: "Title",
          thStyle: { width: "30%" },
        },
        {
          key: "additional_comment",
          label: "Comment",
          thStyle: { width: "20%" },
        },
        {
          key: "commence_date",
          label: "Date",
          thStyle: { width: "10%" },
        },
        {
          key: "meeting_minutes_file",
          label: "File",
          thStyle: { width: "10%" },
        },
      ],
      groupedSubsections: [],
      flatPickrConfig: {
        enableTime: false,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "F j, Y",
      },
    };
  },

  mounted() {
    // this.getOwnerOptions();
  },

  computed: {
    formattedActions() {
      return this.subsection_actions.map((action) => ({
        action_tool_id: action.action_tool_id,
        displayText: {
          pillarTitle: action.pillar_title ? action.pillar_title : "-",
          remainingText: ` - ${
            action.pillar_subtitle ? action.pillar_subtitle : "-"
          } | ${action.objective_title ? action.objective_title : "-"} | ${
            action.action_title
              ? this.truncateText(action.action_title, 100)
              : "-"
          }`,
        },
        original: action,
        customStyles: {
          borderLeft: `3px solid ${action.pillar_color || "#007bff"}`,
          paddingLeft: "8px",
          backgroundColor: `${action.pillar_color}15`,
        },
        pillarColor: action.pillar_color || "#2c3e50",
      }));
    },
  },

  methods: {
    async openModal(wg_id) {
      this.resetSubsectionInputsModal();
      this.selectedWorkingGroupId = wg_id;
      await this.refreshSubsections();
      this.$bvModal.show("subsection-inputs-modal");
    },

    async downloadFile(id) {
      if (id) {
        return new Promise((resolve, reject) => {
          axiosIns
            .get(`/workingGroup/downloadMeetingMinutesFile/${id}`, {
              responseType: "blob",
            })
            .then((response) => {
              const contentType = response.headers["content-type"];
              const url = window.URL.createObjectURL(
                new Blob([response.data], { type: contentType })
              );
              const link = document.createElement("a");
              link.href = url;

              // Get filename from content-disposition header
              const contentDisposition =
                response.headers["content-disposition"];
              let fileName = "meeting-minutes";

              if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(
                  /filename="(.+)"/
                );
                if (fileNameMatch && fileNameMatch.length === 2) {
                  fileName = fileNameMatch[1];
                }
              } else {
                switch (contentType) {
                  case "application/pdf":
                    fileName += ".pdf";
                    break;
                  case "application/vnd.ms-excel":
                    fileName += ".xls";
                    break;
                  case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    fileName += ".xlsx";
                    break;
                  case "application/msword":
                    fileName += ".doc";
                    break;
                  case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                    fileName += ".docx";
                    break;
                  default:
                    fileName += ".txt";
                    break;
                }
              }

              link.setAttribute("download", fileName);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);

              resolve(response);
            })
            .catch((error) => {
              console.error("Download failed:", error);
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  text: "Failed to download file",
                  variant: "danger",
                },
              });
              reject(error);
            });
        });
      }
    },

    resetSubsectionInputsModal() {
      this.subsectionInputsForm = {
        id: null,
        working_group_id: null,
        meeting_title: "",
        owner_id: null,
        commence_date: null,
        end_date: null,
        meeting_minutes_file: null,
        additional_comment: "",
      };
      this.isEditMode = false;
    },

    async handleSubsectionInputsSubmit() {
      const isValid = await this.$refs.subsectionInputRules.validate();

      if (!isValid) return;

      try {
        const formData = new FormData();
        Object.keys(this.subsectionInputsForm).forEach((key) => {
          if (this.subsectionInputsForm[key] !== null) {
            formData.append(key, this.subsectionInputsForm[key]);
          }
        });

        if (this.isEditMode) {
          await this.$store.dispatch("settings/updateSubsection", formData);
        } else {
          await this.$store.dispatch("settings/createSubsection", formData);
        }

        await this.refreshSubsections();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            text: `update ${
              this.isEditMode ? "updated" : "created"
            } successfully`,
            variant: "success",
          },
        });

        if (this.isEditMode) {
          this.isEditMode = false;
          this.resetSubsectionInputsModal();
        }
      } catch (error) {
        console.error("Error:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "An error occurred while processing your request",
            variant: "danger",
          },
        });
      }
    },

    async refreshSubsections() {
      try {
        const response = await this.$store.dispatch(
          "settings/getWorkingGroupToolSubsections",
          this.selectedWorkingGroupId
        );

        this.groupedSubsections = response.data.subsections;
        this.subsection_actions = response.data.subsection_actions;
      } catch (error) {
        console.error("Error fetching update:", error);
        this.groupedSubsections = [];
      }
    },

    editSubsection(item) {
      this.isEditMode = true;
      this.subsectionInputsForm = { ...item };
    },

    async deleteSubsection(id) {
      try {
        await this.$store.dispatch("workingGroup/deleteSubsection", id);
        await this.refreshSubsections();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            text: "Update deleted successfully",
            variant: "success",
          },
        });
      } catch (error) {
        console.error("Error deleting update:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "An error occurred while deleting the update",
            variant: "danger",
          },
        });
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString();
    },

    truncateText(text, maxLength) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + "...";
      }
      return text;
    },

    validateDates() {
      if (
        this.subsectionInputsForm.commence_date &&
        this.subsectionInputsForm.end_date
      ) {
        const commenceDate = new Date(this.subsectionInputsForm.commence_date);
        const endDate = new Date(this.subsectionInputsForm.end_date);

        if (commenceDate > endDate) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Warning",
              text: "Commence date cannot be later than end date",
              variant: "warning",
            },
          });

          this.subsectionInputsForm.commence_date = null;
          this.subsectionInputsForm.end_date = null;
        }
      }
    },

    getOwnerOptions() {
      this.$store.dispatch("settings/getWgOwnerOptions").then((res) => {
        this.ownerOptions = res.data;
      });
    },
  },
};
</script>

<style>
.truncate-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  max-height: 4.5em; /* 1.5 line-height * 3 lines */
}
</style>
